import { useEffect } from 'react';
import { App, Alert, Form, Input, Modal, Select } from 'antd';
import { useUpdateSecret } from '../hooks/useSecrets';
import {
  buildSecret,
  endpointPlaceholder,
  secretData,
  STORAGE_TYPE_OPTIONS,
  type StorageTypeCase,
} from '@/lib/secretStorage';
import { fullErrorText } from '@/api/errors';
import { type Secret } from '@/proto/agentplatform/v2/secret_pb';

interface Props {
  open: boolean;
  credential: Secret | null;
  onClose: () => void;
}

interface FormValues {
  name: string;
  storageType: StorageTypeCase;
  region: string;
  endpoint: string;
}

const ENDPOINT_PATTERN = /^https?:\/\//;

export default function EditObjectStorageCredentialDialog({ open, credential, onClose }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const updateMut = useUpdateSecret();

  useEffect(() => {
    if (open && credential) {
      const body = secretData(credential);
      form.setFieldsValue({
        name: credential.name,
        storageType: (credential.data.case as StorageTypeCase) ?? 'aliyunOss',
        region: body?.region ?? '',
        endpoint: body?.endpoint ?? '',
      });
    }
  }, [open, credential, form]);

  async function onOk() {
    if (!credential) return;
    const values = await form.validateFields();
    try {
      // AK/SK 留空：后端 UpdateSecret 会保留原始密钥，仅更新非脱敏字段。
      await updateMut.mutateAsync({
        id: credential.id,
        secret: buildSecret({
          name: values.name.trim(),
          storageType: values.storageType,
          region: values.region.trim(),
          endpoint: values.endpoint.trim(),
          accessKey: '',
          secretKey: '',
        }),
      });
      message.success('凭证已更新');
      onClose();
    } catch (e) {
      message.error(fullErrorText(e));
    }
  }

  const maskedAccessKey = credential ? secretData(credential)?.accessKey : '';
  const storageType = (credential?.data.case as StorageTypeCase) ?? 'aliyunOss';

  return (
    <Modal
      title="编辑对象存储凭证"
      open={open}
      onCancel={onClose}
      onOk={onOk}
      okText="保存"
      cancelText="取消"
      confirmLoading={updateMut.isPending}
      width={520}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        message="可修改名称、区域和访问地址；存储类型与 AccessKey 不可编辑，Secret Access Key 不展示"
        style={{ marginBottom: 16 }}
      />
      <Form form={form} layout="vertical">
        <Form.Item
          label="凭证名称"
          name="name"
          rules={[{ required: true, message: '凭证名称必填' }]}
        >
          <Input maxLength={64} />
        </Form.Item>

        <Form.Item label="存储类型" name="storageType">
          <Select options={STORAGE_TYPE_OPTIONS} disabled />
        </Form.Item>

        <Form.Item
          label="区域（Region）"
          name="region"
          rules={[{ required: true, message: '区域必填' }]}
        >
          <Input maxLength={128} />
        </Form.Item>

        <Form.Item
          label="访问地址（Endpoint）"
          name="endpoint"
          rules={[
            { required: true, message: '访问地址必填' },
            {
              validator: (_, value: string) =>
                !value || (ENDPOINT_PATTERN.test(value.trim()) && value.trim().length <= 512)
                  ? Promise.resolve()
                  : Promise.reject(new Error('访问地址需以 http(s):// 开头，且长度不超过 512')),
            },
          ]}
        >
          <Input maxLength={512} placeholder={endpointPlaceholder(storageType)} />
        </Form.Item>

        <Form.Item label="AccessKey ID">
          <Input value={maskedAccessKey} disabled autoComplete="off" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
